// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import BusComponent from '../BusComponent';
import { MIN_VERTICES } from '../../helpers/customShape';

const WHOLE_SQ_MI_FROM = 10;
const HINTS = {
    move: 'Drag the shape to move it',
    edit: 'Drag points to reshape · click a midpoint to add one · right-click to remove',
    add: 'Click on the map to add points',
    close: 'Click the first or last point to close, or press Enter',
};

const formatArea = sqMi =>
    `${sqMi >= WHOLE_SQ_MI_FROM ? Math.round(sqMi).toLocaleString() : sqMi.toFixed(1)} sq mi`;

/**
 * The floating toolbar of `PolygonDrawHandler`: live area, what to do next,
 * the reason an edit was refused, and Move / Cancel / Done.
 *
 * @typedef Props
 * @property {string} mapInstanceId
 *
 * @typedef State
 * @property {'draw' | 'edit' | null} mode
 * @property {number} areaSqMi
 * @property {number} vertexCount
 * @property {boolean} moving
 * @property {string | null} error
 *
 * @extends {BusComponent<Props, State>}
 */
class PolygonDrawPanel extends BusComponent {
    constructor(props, context) {
        super(props, context);
        this.state = { mode: null, areaSqMi: 0, vertexCount: 0, moving: false, error: null };
    }

    componentDidMount() {
        this.bindGluBusEvents({ POLYGON_DRAW_STATE: this.onState });
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
    }

    onState = ({ mapInstanceId, mode, areaSqMi, vertexCount, moving, error }) => {
        if (mapInstanceId !== this.props.mapInstanceId) return;
        this.setState({ mode, areaSqMi, vertexCount, moving, error });
    };

    onMove = () => this.emit('POLYGON_DRAW_MOVE_TOGGLE');

    onCancel = () => this.emit('POLYGON_DRAW_CANCEL');

    onDone = () => this.emit('POLYGON_DRAW_CONFIRM');

    hint() {
        const { mode, vertexCount, moving } = this.state;
        if (mode === 'edit') return moving ? HINTS.move : HINTS.edit;
        return vertexCount < MIN_VERTICES ? HINTS.add : HINTS.close;
    }

    render() {
        const { mode, areaSqMi, vertexCount, moving, error } = this.state;
        if (!mode) return null;
        return (
            <div className="polygon-draw-panel" data-testid="polygon-draw-hint">
                <div className="polygon-draw-panel__header">
                    <span className="polygon-draw-panel__area">{formatArea(areaSqMi)}</span>
                    <span className="polygon-draw-panel__hint">{this.hint()}</span>
                </div>
                {error && (
                    <div
                        className="polygon-draw-panel__error"
                        role="alert"
                        data-testid="polygon-draw-error"
                    >
                        <i className="material-icons" aria-hidden="true">
                            error_outline
                        </i>
                        <span>{error}</span>
                    </div>
                )}
                <div className="polygon-draw-panel__actions">
                    {mode === 'edit' && (
                        <button
                            className={classNames('polygon-draw-panel__btn polygon-draw-panel__move', {
                                'polygon-draw-panel__move--active': moving,
                            })}
                            onClick={this.onMove}
                            aria-pressed={moving}
                            data-testid="polygon-move-toggle"
                        >
                            <i className="material-icons" aria-hidden="true">
                                open_with
                            </i>
                            Move
                        </button>
                    )}
                    <button
                        className="polygon-draw-panel__btn polygon-draw-panel__cancel"
                        onClick={this.onCancel}
                        data-testid="polygon-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        className="polygon-draw-panel__btn polygon-draw-panel__done"
                        onClick={this.onDone}
                        disabled={vertexCount < MIN_VERTICES}
                        data-testid="polygon-done"
                    >
                        Done
                    </button>
                </div>
            </div>
        );
    }
}

PolygonDrawPanel.propTypes = {
    mapInstanceId: PropTypes.string.isRequired,
};

export default PolygonDrawPanel;
